import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Sparkles, Compass } from 'lucide-react';

const API_URL = window.location.origin;

const SEASON_THEMES = {
  1: { accent: '#C97B5D', tagline: 'Before we build, we remember.' },
  2: { accent: '#8A9B6E', tagline: 'Wholeness is the way home.' },
  3: { accent: '#B08D46', tagline: 'Identity becomes action.' },
  4: { accent: '#6E7F9B', tagline: 'A life that outlives you.' }
};

export default function SeasonLandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/legacy/seasons`)
      .then(res => setData(res.data))
      .catch(() => {});
  }, []);

  if (!data) return <div className="min-h-screen bg-forest" />;

  const requested = parseInt(searchParams.get('s'), 10);
  const activeNum = [1, 2, 3, 4].includes(requested) ? requested : (data.current_season || 1);
  const season = (data.seasons || []).find(s => s.season === activeNum) || {};
  const theme = SEASON_THEMES[activeNum];
  const isCurrent = data.current_season === activeNum;

  return (
    <div className="min-h-screen bg-forest text-white" data-testid="season-landing-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 70% 20%, ${theme.accent}, transparent 55%)` }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-16">
          <Link to="/legacy" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors" data-testid="season-back-link">
            <ArrowLeft className="h-4 w-4" /> Legacy Circle
          </Link>

          {/* Season navigation */}
          <div className="flex items-center gap-2 mt-8 flex-wrap">
            {(data.seasons || []).map(s => (
              <button key={s.season} onClick={() => setSearchParams({ s: String(s.season) })}
                className={`text-xs px-4 py-2 rounded-full border transition-all ${s.season === activeNum ? 'bg-white text-forest border-white' : 'border-white/25 text-white/70 hover:border-white/60'}`}
                data-testid={`season-nav-${s.season}`}>
                {s.season}. {s.name}
              </button>
            ))}
          </div>

          <div className="mt-10">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-xs uppercase tracking-[0.3em]" style={{ color: theme.accent }}>Season {activeNum} · {season.months}</p>
              {isCurrent && (
                <Badge className="bg-white/10 text-white border border-white/20 text-xs" data-testid="season-current-badge">
                  <Sparkles className="h-3 w-3 mr-1" /> We are here now
                </Badge>
              )}
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-3" data-testid="season-title">{season.name}</h1>
            <p className="font-heading text-lg md:text-xl italic mt-4 text-white/85" data-testid="season-question">"{season.question}"</p>
            <p className="text-sm text-white/50 mt-2">{theme.tagline}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pb-20 space-y-6 -mt-4">
        {season.description && (
          <Card className="bg-white/[0.06] border-white/10 backdrop-blur-sm">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-widest text-white/40 mb-2">This Season</p>
              <p className="text-white/85 leading-relaxed whitespace-pre-line" data-testid="season-description">{season.description}</p>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {season.invitation && (
            <Card className="border-0" style={{ background: `linear-gradient(135deg, ${theme.accent}22, transparent)`, border: `1px solid ${theme.accent}44` }}>
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-widest mb-2" style={{ color: theme.accent }}>The Invitation</p>
                <p className="text-white/85 leading-relaxed whitespace-pre-line" data-testid="season-invitation-text">{season.invitation}</p>
              </CardContent>
            </Card>
          )}
          {season.practices && (
            <Card className="bg-white/[0.06] border-white/10">
              <CardContent className="p-6">
                <p className="text-xs uppercase tracking-widest text-white/40 mb-2">Practices</p>
                <p className="text-white/85 leading-relaxed whitespace-pre-line" data-testid="season-practices-text">{season.practices}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {season.video_url && (
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <div className="aspect-video">
              <iframe src={season.video_url} title={`Season ${activeNum}: ${season.name}`} className="w-full h-full" frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture" allowFullScreen data-testid="season-video" />
            </div>
          </div>
        )}

        {!season.description && !season.invitation && !season.practices && !season.video_url && (
          <Card className="bg-white/[0.06] border-white/10">
            <CardContent className="p-8 text-center">
              <Compass className="h-8 w-8 mx-auto mb-3" style={{ color: theme.accent }} />
              <p className="text-white/70">This season's content is being prepared. The question is already yours to sit with:</p>
              <p className="font-heading text-xl italic mt-2 text-white">"{season.question}"</p>
            </CardContent>
          </Card>
        )}

        <div className="text-center pt-4">
          <Link to="/legacy" className="text-sm text-white/50 hover:text-white transition-colors underline underline-offset-4" data-testid="season-return-hub">
            Return to the Circle
          </Link>
        </div>
      </div>
    </div>
  );
}
